import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import DashboardLayout from '../../app/layouts/dashboard';
import LogoOnlyLayout from '../../app/layouts/LogoOnlyLayout';
import GuestGuard from '../../app/guards/GuestGuard';
import AuthGuard from '../../app/guards/AuthGuard';
import { PATH_AFTER_LOGIN } from '../../app/config';
import { LoadingScreen } from '../components/base';

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};
export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'reset-password', element: <ResetPassword /> },
      ],
    },
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <Dashboard /> },
      ],
    },
    {
      path: 'school',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/school" replace />, index: true },
        { path: 'details', element: <SchoolDetails /> },
      ],
    },
    {
      path: 'class',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/class" replace />, index: true },
        { path: ':id/details', element: <ClassDetails /> },
        { path: 'list', element: <ClassList /> },
      ],
    },
    {
      path: 'teacher',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/teacher" replace />, index: true },
        { path: ':id/details', element: <TeacherDetails /> },
        { path: 'list', element: <TeacherList /> },
      ],
    },
    {
      path: 'parent',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/parent" replace />, index: true },
        { path: 'list', element: <ParentList /> },
      ],
    },
    {
      path: 'event',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/event" replace />, index: true },
        { path: ':id/details', element: <EventDetails /> },
        { path: 'list', element: <EventList /> },
        { path: 'calendar', element: <EventCalendar /> },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ element: <Dashboard />, index: true }],
    },
    {
      path: '/messages',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Messages />, index: true },
        { element: <Messages />, path: 'conversation/:id' },
      ],
    },
    {
      path: '/hula-lab',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ element: <HulaLab />, index: true }],
    },
    {
      path: '/lucy',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ element: <Lucy />, index: true }],
    },
    {
      path: '/tutorials',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [{ element: <Tutorials />, index: true }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
const Login = Loadable(lazy(() => import('../../modules/auth/Login')));
const ResetPassword = Loadable(lazy(() => import('../../modules/auth/ResetPassword')));
const Dashboard = Loadable(lazy(() => import('../../modules/dashboard/Dashboard')));
const Messages = Loadable(lazy(() => import('../../modules/messages/Messages')));
const HulaLab = Loadable(lazy(() => import('../../modules/hula-lab/index')));
const Lucy = Loadable(lazy(() => import('../../modules/lucy/index')));
const Tutorials = Loadable(lazy(() => import('../../modules/tutorials/index')));
const SchoolDetails = Loadable(lazy(() => import('../../modules/school/SchoolDetails')));
const TeacherList = Loadable(lazy(() => import('../../modules/teachers/TeacherList')));
const TeacherDetails = Loadable(lazy(() => import('../../modules/teachers/TeacherDetails')));
const ClassList = Loadable(lazy(() => import('../../modules/classes/ClassList')));
const ClassDetails = Loadable(lazy(() => import('../../modules/classes/ClassDetails')));
const ParentList = Loadable(lazy(() => import('../../modules/parents/ParentList')));
const EventList = Loadable(lazy(() => import('../../modules/events/EventList')));
const EventDetails = Loadable(lazy(() => import('../../modules/events/EventDetails')));
const EventCalendar = Loadable(lazy(() => import('../../modules/events/EventCalendar')));
const Page500 = Loadable(lazy(() => import('../../modules/auth/Page500')));
const Page403 = Loadable(lazy(() => import('../../modules/auth/Page403')));
const Page404 = Loadable(lazy(() => import('../../modules/auth/Page404')));
